import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { apiCreate, apiDelete, apiList, apiUpdate } from "@/lib/api";

export interface ContatoUtil {
  id: string;
  user_id: string;
  nome: string;
  telefone: string;
  categoria: string;
  tipo?: string;
  email?: string;
  endereco?: string;
  horario_funcionamento?: string;
  observacoes?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export const useContatosUteis = () => {
  const queryClient = useQueryClient();

  const { data: contatos = [], isLoading } = useQuery({
    queryKey: ["contatos-uteis"],
    queryFn: async () => {
      const data = await apiList<ContatoUtil>("contatos_uteis");
      return data.sort((a, b) => `${a.categoria}-${a.nome}`.localeCompare(`${b.categoria}-${b.nome}`));
    },
  });

  const createContato = useMutation({
    mutationFn: async (contato: Omit<ContatoUtil, "id" | "user_id" | "created_at" | "updated_at">) => {
      return apiCreate<ContatoUtil>("contatos_uteis", contato);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contatos-uteis"] });
      toast({
        title: "Contato criado",
        description: "O contato foi criado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar contato",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateContato = useMutation({
    mutationFn: async ({ id, ...contato }: Partial<ContatoUtil> & { id: string }) => {
      return apiUpdate<ContatoUtil>("contatos_uteis", id, contato);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contatos-uteis"] });
      toast({
        title: "Contato atualizado",
        description: "O contato foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar contato",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteContato = useMutation({
    mutationFn: async (id: string) => {
      await apiDelete("contatos_uteis", id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contatos-uteis"] });
      toast({
        title: "Contato excluído",
        description: "O contato foi excluído com sucesso.",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir contato",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    contatos,
    isLoading,
    createContato: createContato.mutate,
    updateContato: updateContato.mutate,
    deleteContato: deleteContato.mutate,
  };
};
